<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import _ from "underscore";
import moment from "moment";
import { computed, nextTick, onMounted, watch } from "vue";

import { useIoaStore } from "@/stores/ioa-store";
import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useFiltersStore } from "@/stores/filters-store";
import { useMessageStore } from "@/stores/message-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { filterSetService } from "@/services";
import { getGracePeriod, regExpEscape, toMoment } from "@/services/UtilitiesService";

import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import { RsCheckbox, RsCheckboxGroup, RsManageTags } from "@/components";

import type { Moment } from "moment";
import type AgreementPeriod from "@/models/AgreementPeriod";
import type { DateRangeFilter, Filter } from "@/models/Filter";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const filtersStore = useFiltersStore();
const routeParams = useCaseRouteParams();

let dateRangeFrom = _ref<string>("");
let dateFromMessage = _ref<{ message: string; className: string } | null>(null);
let dateRangeTo = _ref<string>("");
let dateToMessage = _ref<{ message: string; className: string } | null>(null);
let dateRangeIncBlanks = _ref<boolean>(true);
const classificationFilter = _ref<string>("");

let showNewFilterSet = _ref<boolean>(false);
let newFilterSetName = _ref<string>("");
let filterSetStatus = _ref<"IDLE" | "LOADING" | "ERROR">("IDLE");
let filterSetMessage = _ref<string>("");
let isPromoted = _ref<boolean>(false);
let summariserStatusOptions = _ref<string[]>([]);

const canCreateBrandFilterSet = _computed(() => {
  return useUserPermissionStore().userPermission.canCreateBrandFilterSet ?? false;
});

const filterSets = _computed(() => {
  return filtersStore.filterSets;
});

const selectedFilterSetName = _computed(() => {
  return filtersStore.selectedFilterSet?.name;
});
const selectedFilterSet = _computed(() => {
  return filtersStore.selectedFilterSet;
});

const filtersConfig = _computed(() => {
  return useBrandStore().viewerConfig.filters || { ioa: { label: "", disabled: false } };
});

const hideDuplicates = _computed(() => {
  return filtersStore.filter.hideDuplicates;
});

const hideDrafts = _computed(() => {
  return filtersStore.filter.hideDrafts;
});

const classifications = _computed(() => {
  const allClassifications = Object.entries(useBrandStore().classifications).flatMap(([key, value]) => {
    return [key, ...(value.subClassifications?.map((c) => c.name) || [])];
  });

  return [...new Set(allClassifications)];
});

const usedClassifications = _computed(() => {
  const filter = new RegExp(`${regExpEscape(classificationFilter.value) || ".*"}`, "i");
  const items: string[] = useMetadataStore()
    .files.flatMap((f) =>
      f.aiClassification === f.aiClassificationGroup
        ? [f.aiClassification || "Unknown"]
        : [f.aiClassification || "Unknown", f.aiClassificationGroup || "Unknown"],
    )
    .sort();

  const itemSet = _.countBy(items);
  return classifications.value
    .filter((f) => filter.test(f))
    .map((k) => {
      return {
        value: k,
        text: `${k} (${itemSet[k] || 0})`,
      };
    });
});

const summariserStatuses = _computed(() => {
  return summariserStatusOptions.value.map((s) => ({ value: s, text: s }));
});

const hasAgreementPeriods = _computed(() => {
  return agreementPeriods.value.length > 0;
});

const agreementPeriods = _computed(() => {
  const FORMAT = "YYYY MM DD";

  return useCaseStore()
    .config.agreementPeriods.filter((ap) => ap.from && ap.to)
    .map(getGracePeriod)
    .sort((a, b) => {
      const aInstance = moment(a.from, FORMAT, true);
      const bInstance = moment(b.from, FORMAT, true);

      if (aInstance.isAfter(bInstance)) {
        return 1;
      }
      return -1;
    });
});

const hasEvidenceCounts = _computed(() => {
  return evidenceCounts.value.length > 0;
});

const evidenceCounts = _computed(() => {
  const activeLabels = useBrandStore().evidenceCountLabels.map((name) => ({ name, value: name }));
  const usedLabels = useMetadataStore().files.flatMap((m) => m.evidenceCounts || [{ name: "No Indicators", value: 0 }]);

  return [...activeLabels, ...usedLabels]
    .reduce<Array<{ text: string; value: string }>>((acc, cur) => {
      if (acc.find((item) => item.text === cur.name) != null) return acc;
      return [...acc, { text: cur.name, value: String(cur.value || cur.name) }];
    }, [])
    .sort((a, b) => a.text.localeCompare(b.text));
});

const hasInstancesOfAdvice = _computed(() => {
  return useIoaStore().instanceOfAdvices.length > 0;
});

const adviceInstances = _computed(() => {
  const items: Array<{
    instanceReference: string;
    minDate: Moment;
    fileCount: number;
  }> = [];

  useIoaStore().instanceOfAdvices.forEach((ai) => {
    let item = items.find((f) => f.instanceReference === ai.instanceReference);

    if (!item) {
      item = {
        instanceReference: ai.instanceReference,
        minDate: moment(),
        fileCount: ai.fileReferences.length,
      };
      items.push(item);
    }
    useMetadataStore()
      .files.filter((m) => _.any(ai.fileReferences, m.fileReference))
      .forEach((m) => {
        const itemDate = toMoment(m.edxIssueDate || "", true) || moment();
        if (item && itemDate.isBefore(item.minDate)) {
          item.minDate = itemDate;
        }
      });
  });
  return items
    .sort((l, r) => l.minDate.valueOf() - r.minDate.valueOf())
    .map((m, index) => {
      return {
        instanceReference: m.instanceReference,
        id: String(index + 1),
        fileCount: m.fileCount,
      };
    });
});

const isSummariserEnabled = _computed(() => {
  return (
    useBrandStore().viewerConfig.recordList?.summariser || {
      enabled: false,
    }
  ).enabled;
});

function validateDateFrom(): string {
  dateFromMessage.value = null;
  if (dateRangeFrom.value === "") {
    return "";
  }

  const result = validateDate(dateRangeFrom.value);

  dateFromMessage.value = result;
  return result.className;
}

function validateDateTo(): string {
  dateToMessage.value = null;
  if (dateRangeTo.value === "") {
    return "";
  }

  const result = validateDate(dateRangeTo.value);

  dateToMessage.value = result;

  if (result.className !== "") {
    return result.className;
  }

  if (dateRangeFrom.value !== "") {
    const from: Moment | null = toMoment(dateRangeFrom.value, true);
    const to: Moment | null = toMoment(dateRangeTo.value, false);
    if (from === null || to === null) {
      return "";
    }
    if (to.isBefore(from)) {
      dateToMessage.value = {
        message: "To date cannot be earlier than from date",
        className: "tw-is-input-invalid",
      };
      return dateToMessage.value.className;
    }
  }

  return "";
}

const selectedClassifications = _computed({
  get() {
    return filtersStore.filter.classifications;
  },
  set(values: string[]) {
    onClassificationChanged(values);
  },
});

const selectedSummariserStatuses = _computed({
  get() {
    return filtersStore.filter.summariserStatuses || ["Any"];
  },
  set(values: string[]) {
    onSummariserStatusChanged(values);
  },
});

onMounted(() => {
  dateRangeIncBlanks.value = filtersStore.filter.dateRange.includeBlanks;
  dateRangeFrom.value = filtersStore.filter.dateRange.from;
  dateRangeTo.value = filtersStore.filter.dateRange.to;
  nextTick(
    () =>
      (summariserStatusOptions.value = [
        "Records processed by Evidence AI",
        "Records not processed by Evidence AI",
        "Records with bookmarks",
        "Records with outstanding AI Evidence",
      ]),
  );
});

watch((selectedFilterSet), (to) => storeDateRanges(to?.value.dateRange), { immediate: true, deep: true });

watch(
  () => useFiltersStore().filter.dateRange,
  (to) => storeDateRanges(to),
);

function storeDateRanges(data: DateRangeFilter | undefined) {
  dateRangeFrom.value = data?.from || "";
  dateRangeTo.value = data?.to || "";
  dateRangeIncBlanks.value = data?.includeBlanks ?? true;
}

function onDuplicateChange(value: boolean) {
  filtersStore.changeDuplicateValue({ hideDuplicates: value });
}

function onDraftChange(value: boolean) {
  filtersStore.changeDraftValue({ hideDrafts: value });
}

function onDateRangeSelected() {
  if (validateDateFrom() !== "" || validateDateTo() !== "") {
    return;
  }

  filtersStore.changeDateRange({
    to: dateRangeTo.value,
    from: dateRangeFrom.value,
    includeBlanks: dateRangeIncBlanks.value,
  });
}

function onDateRangeIncludeBlanksChange(value: boolean) {
  dateRangeIncBlanks.value = value;

  filtersStore.changeDateRange({
    to: dateRangeTo.value,
    from: dateRangeFrom.value,
    includeBlanks: value,
  });
}

function onClassificationChanged(value: string[]) {
  filtersStore.changeClassifications({ classifications: value });
}

function onSummariserStatusChanged(value: string[]) {
  filtersStore.changeSummariserStatuses({ statuses: value });
}

function onAgreementPeriodSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target?.value || "";

  const period: AgreementPeriod | undefined = agreementPeriods.value.find((ap) => String(ap.id) === String(value));
  if (!period) {
    filtersStore.removeAgreementPeriod();
    return;
  }

  filtersStore.addAgreementPeriod(period);
}

const selectedEvidenceCounts = computed({
  get() {
    return filtersStore.filter.evidenceCounts;
  },
  set(values: string[]) {
    filtersStore.changeEvidenceCounts({ evidenceCounts: values });
  },
});

function onInstanceSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target?.value || "";
  filtersStore.changeAdviceInstanceId({ instanceReference: value });
}

async function saveAsNewFilter(): Promise<void> {
  const name = newFilterSetName.value;
  if (!name) {
    filterSetMessage.value = "Name cannot be empty";
    filterSetStatus.value = "ERROR";
    return;
  }
  if (filtersStore.filterSets.find((f) => f.name === name)) {
    filterSetMessage.value = "Filter set with same name already exists.";
    filterSetStatus.value = "ERROR";
    return;
  }
  await saveFilters(name);
  filtersStore.selectFilterSet(name);
  showNewFilterSet.value = false;
  newFilterSetName.value = "";
}
async function saveFilters(name: string | undefined): Promise<void> {
  if (!name) {
    filterSetMessage.value = "Name cannot be empty";
    filterSetStatus.value = "ERROR";
    return;
  }

  const filters = JSON.parse(JSON.stringify(filtersStore.filter)) as Filter;
  filterSetMessage.value = "";
  filterSetStatus.value = "LOADING";

  try {
    await filterSetService.save(routeParams.value.client, routeParams.value.brand, name, filters);
    if (isPromoted.value !== !!selectedFilterSet.value?.promoted) {
      await promoteFilterSet(isPromoted.value);
    }
    useMessageStore().updateMessage({
      message: `Filter saved successfully`,
      title: "Success!",
      type: "info",
      autoHide: true,
      showReload: false,
    });

    filterSetMessage.value = "";
    filterSetStatus.value = "IDLE";
  } catch (e) {
    filterSetMessage.value = "Error occurred while saving. Please try again later.";
    filterSetStatus.value = "ERROR";
  } finally {
    await filtersStore.load({ isRefresh: true });
    filtersStore.selectFilterSet(selectedFilterSetName.value);
  }
}

async function deleteFilterSet(name: string | undefined): Promise<void> {
  if (!name) return;
  filterSetStatus.value = "LOADING";

  try {
    await filterSetService.deleteFilterSet(routeParams.value.client, routeParams.value.brand, name);
  } finally {
    // Reset and reload filters
    await filtersStore.load({ isRefresh: true });
    filtersStore.selectFilterSet("");
    filtersStore.deleteFilterSet(name);
    filterSetStatus.value = "IDLE";
  }
}

async function promoteFilterSet(promote: boolean): Promise<void> {
  if (!selectedFilterSetName.value) return;
  const name = selectedFilterSetName.value;

  if (promote) {
    await filterSetService.promote(routeParams.value.client, routeParams.value.brand, name);
  } else {
    await filterSetService.demote(routeParams.value.client, routeParams.value.brand, name);
  }
}

function onFilterSetSelected(event: Event | null) {
  const value = (event?.target as HTMLSelectElement)?.value || "";
  filtersStore.selectFilterSet(value);
  isPromoted.value = !!selectedFilterSet.value?.promoted;
  filterSetStatus.value = "IDLE";
  filterSetMessage.value = "";
}

function reset() {
  dateRangeFrom.value = "";
  dateRangeTo.value = "";
  dateRangeIncBlanks.value = true;

  onFilterSetSelected(null);
  filtersStore.resetTriggered();
}

function toggleNewFilterSet() {
  showNewFilterSet.value = !showNewFilterSet.value;
  filterSetStatus.value = "IDLE";
  filterSetMessage.value = "";
}

function validateDate(date: string): { className: string; message: string } {
  let format;
  switch (date.split(" ").length) {
    case 1:
      format = "YYYY";
      break;
    case 2:
      format = "YYYY MM";
      break;
    default:
      format = "YYYY MM DD";
      break;
  }

  const momentInstance = moment(date, format, true);

  if (!momentInstance.isValid()) {
    return {
      message: `Date should be in 'YYYY' or 'YYYY MM' or 'YYYY MM DD' format`,
      className: "tw-is-input-invalid",
    };
  }

  if (momentInstance.isAfter(moment())) {
    return {
      message: `Date cannot be greater than ${moment().format("YYYY MM DD")}`,
      className: "tw-is-input-invalid",
    };
  }

  if (momentInstance.isBefore(moment("1900 01 01", "YYYY MM DD", true))) {
    return {
      message: `Date cannot be earlier than ${moment("1900 01 01", "YYYY MM DD", true).format("YYYY MM DD")}`,
      className: "tw-is-input-invalid",
    };
  }

  return { message: "", className: "" };
}
</script>
<template>
  <div
    class="tw-flex tw-size-full tw-flex-wrap tw-content-start tw-items-start tw-gap-2.5 tw-overflow-y-auto tw-bg-gray-100 tw-p-3"
    :class="dockview == null && '[&>*]:tw-max-h-64'"
  >
    <div
      v-show="!showNewFilterSet"
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <div class="tw-flex tw-flex-col tw-flex-wrap tw-justify-between tw-gap-2">
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label for="selectFilterSet">Predefined Filter Sets</label>
          <div class="tw-flex tw-items-center tw-gap-1">
            <select
              id="selectFilterSet"
              class="tw-b-r tw-rounded-md tw-px-2 tw-py-1"
              :value="selectedFilterSetName || ' '"
              @change.prevent="onFilterSetSelected"
            >
              <option value=" ">Select Filter Set</option>
              <option v-for="filterSet in filterSets" :key="filterSet.name" :value="filterSet.name">
                {{ filterSet.name }}
              </option>
            </select>
            <ConfirmPopover
              v-if="selectedFilterSetName"
              :key="selectedFilterSetName"
              confirm-text="Delete"
              :disabled="filterSetStatus === 'LOADING'"
              @confirmed="() => deleteFilterSet(selectedFilterSetName)"
            >
              <template #trigger>
                <StringTooltip item="Delete this filter set" #="{ item }">
                  <div
                    class="tw-ml-1"
                    :aria-label="item"
                    :class="filterSetStatus === 'LOADING' ? 'tw-cursor-not-allowed tw-opacity-50' : ''"
                  >
                    <i aria-hidden="true" class="fa fa-trash"></i>
                  </div>
                </StringTooltip>
              </template>
              <template #question>Are you sure you want to delete "{{ selectedFilterSetName }}"?</template>
            </ConfirmPopover>
          </div>
        </div>
        <RsCheckbox
          v-if="selectedFilterSet"
          id="global-filter"
          v-model="isPromoted"
          :disabled="!canCreateBrandFilterSet"
        >
          Global filter
        </RsCheckbox>
        <div class="tw-flex tw-items-end tw-gap-1">
          <button type="button" aria-label="Reset filters" class="tw-b-r tw-btn-md-white" @click="reset">Reset</button>
          <button
            v-if="selectedFilterSetName"
            type="button"
            class="tw-b-r tw-btn-md-gray"
            aria-label="Save filters to the selected filter set"
            :class="filterSetStatus === 'LOADING' ? 'tw-cursor-not-allowed tw-opacity-50' : ''"
            :disabled="!!(filterSetStatus === 'LOADING' || (selectedFilterSet?.promoted && !canCreateBrandFilterSet))"
            @click="saveFilters(selectedFilterSetName)"
          >
            Update
          </button>
          <button
            type="button"
            class="tw-b-r tw-btn-md-gray"
            aria-label="Create a new filter set with applied filters"
            :class="filterSetStatus === 'LOADING' ? 'tw-cursor-not-allowed tw-opacity-50' : ''"
            :disabled="filterSetStatus === 'LOADING'"
            @click.prevent.stop="toggleNewFilterSet"
          >
            Create
          </button>
        </div>
      </div>
      <div v-if="filterSetMessage" :class="{ 'tw-text-red-700': filterSetStatus === 'ERROR' }">
        {{ filterSetMessage }}
      </div>
    </div>
    <div
      v-show="showNewFilterSet"
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <div class="tw-mr-4 tw-flex tw-flex-col tw-justify-between">
        <div class="tw-flex tw-flex-col">
          <label>Filter Set Name</label>
          <input
            ref="filterRef"
            v-model="newFilterSetName"
            type="text"
            class="tw-form-input"
            :class="{ 'tw-is-input-invalid': filterSetStatus === 'ERROR' }"
          />
        </div>
        <div v-if="filterSetMessage" :class="{ 'tw-text-red-700': filterSetStatus === 'ERROR' }">
          {{ filterSetMessage }}
        </div>
      </div>
      <div class="tw-mt-4 tw-flex tw-justify-end">
        <button class="tw-b-r tw-btn-md-white tw-mr-1" @click="toggleNewFilterSet">Cancel</button>
        <button
          class="tw-b-r tw-btn-md-gray"
          :class="filterSetStatus === 'LOADING' ? 'tw-cursor-not-allowed tw-opacity-50' : ''"
          :disabled="filterSetStatus === 'LOADING'"
          @click="saveAsNewFilter"
        >
          Create
        </button>
      </div>
    </div>
    <div
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <div v-if="hasAgreementPeriods" class="tw-mb-4 tw-flex tw-flex-col">
        <label>Agreement Period</label>
        <select class="tw-b-r tw-px-2 tw-py-1" @change.prevent="onAgreementPeriodSelected">
          <option>Any</option>
          <option
            v-for="ap in agreementPeriods"
            :key="ap.id"
            :value="ap.id"
            :selected="filtersStore.filter.agreementPeriod?.id === ap.id"
          >
            {{ ap.from }} - {{ ap.to }}
            {{ ap.graceMonths ? `[${ap.graceTo}]` : "" }}
          </option>
        </select>
      </div>
      <div class="tw-mb-4 tw-flex tw-flex-col">
        <div tabindex="0">Issue Date <span class="tw-text-xs tw-text-gray-600">(YYYY MM DD)</span></div>
        <div class="tw-flex tw-gap-2">
          <div class="tw-flex tw-flex-1 tw-flex-col">
            <label for="fromDate">From</label>
            <input
              id="fromDate"
              v-model="dateRangeFrom"
              type="text"
              class="tw-form-input"
              aria-label="Issue Date Start. In YYYY MM DD format"
              :class="dateFromMessage?.className"
              @blur.prevent="onDateRangeSelected"
              @keypress.enter="onDateRangeSelected"
            />
            <label v-if="dateFromMessage" for="fromDate" class="tw-mt-1 tw-text-red-700 empty:tw-hidden">
              {{ dateFromMessage.message }}
            </label>
          </div>
          <div class="tw-flex tw-flex-1 tw-flex-col">
            <label for="toDate">To</label>
            <input
              id="toDate"
              v-model="dateRangeTo"
              type="text"
              class="tw-form-input"
              aria-label="Issue Date End. In YYYY MM DD format"
              :class="dateToMessage?.className"
              @blur.prevent="onDateRangeSelected"
              @keypress.enter="onDateRangeSelected"
            />
            <label v-if="dateToMessage" for="toDate" class="tw-mt-1 tw-text-red-700 empty:tw-hidden">
              {{ dateToMessage.message }}
            </label>
          </div>
          <RsCheckbox
            id="dateRange-incblanks"
            class="tw-ml-1"
            :model-value="dateRangeIncBlanks"
            @change="onDateRangeIncludeBlanksChange"
          >
            Inc Blanks
          </RsCheckbox>
        </div>
      </div>
      <div v-if="hasInstancesOfAdvice && !filtersConfig.ioa.disabled" class="tw-flex tw-flex-col">
        <label>{{ filtersConfig.ioa.label || "Instance of Advice" }}</label>
        <select class="tw-b-r tw-px-2 tw-py-1" @change.prevent="onInstanceSelected">
          <option>Any</option>
          <option
            v-for="ai in adviceInstances"
            :key="ai.id"
            :selected="!!(filtersStore.filter.adviceInstanceId === ai.instanceReference)"
            :value="ai.instanceReference"
          >
            {{ ai.id }} - ({{ ai.fileCount }} file{{ ai.fileCount > 1 ? "s" : "" }})
          </option>
        </select>
      </div>
    </div>
    <div
      v-if="hasEvidenceCounts"
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <label>Indicators</label>
      <RsCheckboxGroup
        v-model="selectedEvidenceCounts"
        stacked
        name="evidenceCount"
        class="filter-container tw-mx-auto tw-overflow-auto"
        :options="evidenceCounts"
      />
    </div>
    <div
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <label for="input-classifications">Classifications</label>
      <div class="tw-relative">
        <input
          id="input-classifications"
          v-model.trim="classificationFilter"
          class="tw-form-input tw-mb-1"
          type="text"
          placeholder="Search classifications"
        />
        <i
          v-if="classificationFilter"
          class="fa fa-trash icon clickable-icon tw-text-red-700"
          @click.prevent="classificationFilter = ''"
        />
      </div>
      <RsCheckboxGroup
        v-model="selectedClassifications"
        stacked
        name="classications"
        class="filter-container tw-mx-auto tw-overflow-auto"
        :options="usedClassifications"
      />
    </div>
    <div
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <RsManageTags primary-mode="filterTags"></RsManageTags>
    </div>
    <div
      v-if="isSummariserEnabled"
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <label>Evidence</label>
      <div class="tw-relative">
        <div class="filter-container tw-mx-auto tw-overflow-auto">
          <RsCheckboxGroup
            v-model="selectedSummariserStatuses"
            :options="summariserStatuses"
            name="summariserStatuses"
            stacked
          />
        </div>
      </div>
    </div>
    <div
      class="tw-flex tw-max-h-full tw-flex-col tw-justify-between tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <RsCheckbox id="duplicates" :model-value="hideDuplicates" @change="onDuplicateChange">
        Hide all duplicate files
      </RsCheckbox>
      <RsCheckbox id="drafts" :model-value="hideDrafts" @change="onDraftChange">Hide all draft files</RsCheckbox>
    </div>
  </div>
</template>
<style scoped lang="scss">
.icon {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 10;
  & .clickable-icon {
    cursor: pointer;
  }
}

.filter-container {
  &.custom-checkbox {
    margin-bottom: 2px;
  }
}
</style>
